import BoxedChartHeader from "components/charts/modern/abstract/BoxedChartHeader";
import PlotTypeSelector from "components/charts/modern/inputs/PlotTypeSelector";
import type { GeographicalCountry } from "components/company/sections/summary/adornments/GeographicalCountryStack";
import GeographicalCountryStack from "components/company/sections/summary/adornments/GeographicalCountryStack";
import OnlyPremiumRender from "components/conversion/OnlyPremiumRender";
import ContentContainerBox from "components/stocks/ContentContainerBox";
import IndicatorSelect from "components/stocks/modern/OperatingSegments/IndicatorSelect";
import OperatingSegmentsList from "components/stocks/modern/OperatingSegments/OperatingSegmentsList";
import SingleValueSelect from "components/stocks/modern/OperatingSegments/SingleValueSelect";
import calculateFilterOptions from "components/stocks/modern/OperatingSegments/calculateFilterOptions";
import type { FinancialSegmentsIndicatorName } from "components/stocks/modern/OperatingSegments/genIndicatorsByNameMap";
import genIndicatorsByNameMap from "components/stocks/modern/OperatingSegments/genIndicatorsByNameMap";
import { genStandardizedCountries } from "components/stocks/modern/OperatingSegments/genStandardizedCountries";
import { useIndicatorType, usePlotType, useSelectedYear } from "components/stocks/modern/OperatingSegments/hooks";
import type { FinancialSegmentsFields } from "components/stocks/modern/OperatingSegments/types";
import AllSegmentsHistoryColumnsChart from "components/stocks/modern/chart/AllSegmentsHistoryColumnsChart";
import SegmentsPieChart from "components/stocks/modern/chart/SegmentsPieChart";
import getYear from "date-fns/getYear";
import parseISO from "date-fns/parseISO";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import { BarChartIcon, PieChartIcon } from "lucide-react";
import Link from "next/link";
import { invariant } from "outvariant";
import { useEffect } from "react";
import type { IntlShape } from "react-intl";
import { FormattedMessage, useIntl } from "react-intl";

interface OperatingSegmentsProps {
  kind: "GEOGRAPHIC" | "BUSINESS";
  tickerSymbols: string[];
  financialSegments: FinancialSegmentsFields;
}

export default function OperatingSegments({ kind, tickerSymbols, financialSegments }: OperatingSegmentsProps) {
  const intl = useIntl();
  const [plotType, setPlotType] = usePlotType();
  const [selectedYear, setSelectedYear] = useSelectedYear();
  const [indicatorType, setIndicatorType] = useIndicatorType();

  const isGeographic = kind === "GEOGRAPHIC";
  const indicatorsMap = genIndicatorsByNameMap(intl);
  const standardizedCountries = genStandardizedCountries(intl);
  const indicatorsOrder = Object.keys(indicatorsMap);
  const filterOptions = calculateFilterOptions(financialSegments, indicatorsOrder);
  const allSegments = [...(financialSegments?.segments || [])];

  useEffect(() => {
    if (filterOptions.allYears.length > 0) {
      setSelectedYear(filterOptions.allYears[0]);
      setIndicatorType(filterOptions.indicatorsByYear[filterOptions.allYears[0]][0]);
    }
  }, [tickerSymbols]);

  if (filterOptions.allYears.length === 0) {
    return null;
  }

  invariant(filterOptions.allYears[0], "No fiscal years available");
  invariant(filterOptions.indicatorsByYear[filterOptions.allYears[0]], "No indicators available");

  if (!indicatorType || !selectedYear || !filterOptions.indicatorsByYear[selectedYear]) {
    return null;
  }

  const indicatorDetails = indicatorsMap[indicatorType as FinancialSegmentsIndicatorName];

  if (!indicatorDetails) {
    return null;
  }

  const segments = allSegments
    .map((node) => {
      const ratioIndicator = node.ratioAdjustedIndicators.find((node) => node.name === indicatorType);
      const financialIndicator = node.financialIndicators.find((node) => node.name === indicatorType);

      if (!ratioIndicator && !financialIndicator) {
        return null;
      }

      return {
        ...node,
        ratioAdjustedIndicators: node.ratioAdjustedIndicators.map((indicator) => {
          return {
            ...indicator,
            history: indicator.history.filter((item) => {
              return getYear(parseISO(item.periodEndingDate)) === selectedYear;
            }),
          };
        }),
        financialIndicators: node.financialIndicators.map((indicator) => {
          return {
            ...indicator,
            history: indicator.history.filter((item) => {
              return getYear(parseISO(item.periodEndingDate)) === selectedYear;
            }),
          };
        }),
        ratioIndicator: ratioIndicator
          ? {
              ...ratioIndicator,
              history: ratioIndicator.history.filter((item) => {
                return getYear(parseISO(item.periodEndingDate)) === selectedYear;
              }),
            }
          : null,
        financialIndicator: financialIndicator
          ? {
              ...financialIndicator,
              history: financialIndicator.history.filter((item) => {
                return getYear(parseISO(item.periodEndingDate)) === selectedYear;
              }),
            }
          : null,
      };
    })
    .filter(notUndefinedOrNull)
    .sort((a, b) => {
      return (b.financialIndicator?.value || 0) - (a.financialIndicator?.value || 0);
    });

  return (
    <ContentContainerBox className="flex h-full flex-col space-y-6">
      {isGeographic && (
        <div className="flex flex-col items-start justify-between space-y-4 sm:flex-row sm:items-center sm:space-y-0">
          <BoxedChartHeader
            paywallDisplayOrigin="Geographic segments chart"
            tickerSymbol={tickerSymbols[0]}
            title={<FormattedMessage defaultMessage="Receitas • Países" id="LF00bg" />}
            subtitle={<FormattedMessage defaultMessage="Em quais países a empresa ganha dinheiro?" id="vBSDkK" />}
          />

          <GeographicalCountryStack
            size={32}
            limit={5}
            geographicalCountries={segments
              .map((segment): GeographicalCountry | null => {
                const standardizedCountry = standardizedCountries.find((candidate) => {
                  return candidate.morningstarStandardizedName === segment.name;
                });
                if (!standardizedCountry) {
                  return null;
                }
                return {
                  isoCode: standardizedCountry.isoCountryCode,
                  name: standardizedCountry.name,
                  flagSvg: {
                    ovalHref: `https://s.fndm.to/svg/common/flags/${standardizedCountry.isoCountryCode}.svg`,
                  },
                };
              })
              .filter(notUndefinedOrNull)}
          />
        </div>
      )}

      {kind === "BUSINESS" && (
        <BoxedChartHeader
          paywallDisplayOrigin="Business segments chart"
          tickerSymbol={tickerSymbols[0]}
          title={<FormattedMessage defaultMessage="Receitas • Negócios" id="2qEoKo" />}
          subtitle={
            <FormattedMessage
              defaultMessage="Com quais linhas de produtos/negócios a empresa ganha dinheiro?"
              id="VPSLcG"
            />
          }
        />
      )}

      <OnlyPremiumRender>
        <div className="grid h-full grid-cols-1 items-start space-y-8 overflow-hidden sm:grid-cols-[max-content,1fr] sm:space-x-8">
          <div className="w-full space-y-4 pl-1.5 sm:w-96 sm:max-w-md">
            <PlotTypeSelector
              value={plotType}
              onChange={setPlotType}
              className="w-full rounded-xl sm:w-full sm:max-w-sm"
              itemClassName="h-8 w-full sm:w-full"
              types={[
                {
                  type: "pie",
                  label: intl.formatMessage({
                    id: "9RIsSS",
                    defaultMessage: "Pizza",
                    description: "Tipo de visualização do gráfico",
                  }),
                  icon: <PieChartIcon />,
                },
                {
                  type: "column",
                  label: intl.formatMessage({
                    id: "hZ073v",
                    defaultMessage: "Barra",
                    description: "Tipo de visualização do gráfico",
                  }),
                  icon: <BarChartIcon />,
                },
              ]}
            />
            <SingleValueSelect
              disabled={filterOptions.allYears.length <= 1 || plotType === "column"}
              values={filterOptions.allYears.map((node) => ({
                label: node.toString(),
                value: node.toString(),
                description: intl.formatList(
                  filterOptions.indicatorsByYear[node].map((indicator) => {
                    return indicatorsMap[indicator as FinancialSegmentsIndicatorName].abbreviation;
                  }),
                  {
                    style: "long",
                  },
                ),
              }))}
              onChange={(value) => {
                const year = parseFloat(value);
                const availableIndicators = filterOptions.indicatorsByYear[year];

                setSelectedYear(year);

                if (!availableIndicators.includes(indicatorType)) {
                  setIndicatorType(availableIndicators[0]);
                }
              }}
              value={String(selectedYear)}
            />
            <IndicatorSelect
              value={indicatorType}
              onChange={(indicator) => {
                const availableIndicators = filterOptions.indicatorsByYear[selectedYear];

                setIndicatorType(indicator);

                if (!availableIndicators.includes(indicator)) {
                  setSelectedYear(filterOptions.byIndicator[indicator][0]);
                }
              }}
              indicators={filterOptions.indicatorsByYear[selectedYear]
                .map((indicator) => ({
                  name: indicator,
                  label: indicatorsMap[indicator as FinancialSegmentsIndicatorName].name,
                  description: indicatorsMap[indicator as FinancialSegmentsIndicatorName].description,
                }))
                .sort((a, b) => {
                  return indicatorsOrder.indexOf(a.name) - indicatorsOrder.indexOf(b.name);
                })}
            />
            <OperatingSegmentsList
              kind={kind}
              forYear={selectedYear}
              financialSegments={financialSegments}
              segments={segments}
            />
          </div>

          <div className="flex h-max flex-1 flex-col space-y-4 sm:flex sm:h-[576px]">
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="text-left text-xl font-black text-slate-800 sm:text-2xl">{indicatorDetails.name}</h1>
                <span className="rounded-md bg-orange-50 px-2 py-1 font-mono text-sm font-bold text-orange-600">
                  {plotType === "column" ? (
                    <FormattedMessage description="Gráfico Histórico" defaultMessage="Histórico" id="bLQ/QS" />
                  ) : (
                    selectedYear
                  )}
                </span>
              </div>

              <p className="inline-flex items-center space-x-1.5 text-sm italic tracking-wide text-slate-500">
                {indicatorDetails.description}
              </p>

              {financialSegments.reportSource && (
                <Link
                  className="ml-1 text-sm italic tracking-wide text-blue-600 no-underline hover:underline"
                  href={financialSegments.reportSource.primaryDocumentHref}
                  target="_blank"
                >
                  <FormattedMessage
                    description="Link para o documento fonte das fontes de receitas"
                    defaultMessage="Fonte"
                    id="xGnRNu"
                  />
                </Link>
              )}
            </div>

            <div className="flex h-full items-center rounded-xl bg-slate-50/50">
              {segments.length > 0 && plotType === "pie" && (
                <SegmentsPieChart
                  className="h-full w-80"
                  segments={getAllSegmentsForPieChart(kind, indicatorType, selectedYear, allSegments, intl)}
                />
              )}

              {segments.length > 0 && filterOptions.allYears.length > 0 && plotType === "column" && (
                <AllSegmentsHistoryColumnsChart
                  className="h-full w-full"
                  kind={kind}
                  segments={getAllSegmentsForColumnChart(
                    kind,
                    indicatorType,
                    selectedYear,
                    allSegments,
                    intl,
                    isGeographic ? 5 : undefined,
                  )}
                />
              )}
            </div>
          </div>
        </div>
      </OnlyPremiumRender>
    </ContentContainerBox>
  );
}

const getAllSegmentsForPieChart = (
  kind: "GEOGRAPHIC" | "BUSINESS",
  selectedIndicator: string,
  selectedYear: number,
  segments: FinancialSegmentsFields["segments"],
  intl: IntlShape,
  limit: number = kind === "GEOGRAPHIC" ? 5 : 10,
) => {
  const standardizedCountries = genStandardizedCountries(intl);
  const isGeographic = kind === "GEOGRAPHIC";

  const allSegments = segments
    .map((node) => {
      const financialIndicator = node.ratioAdjustedIndicators.find(({ name }) => {
        return name === selectedIndicator;
      });
      if (!financialIndicator) {
        return null;
      }
      const historyNode = financialIndicator.history.find((node) => {
        return getYear(parseISO(node.periodEndingDate).getTime()) === selectedYear;
      });
      if (!historyNode) {
        return null;
      }

      const standardizedCountry = standardizedCountries.find((candidate) => {
        return candidate.morningstarStandardizedName === node.name;
      });

      return {
        name: isGeographic && standardizedCountry ? standardizedCountry.name : node.name,
        value: historyNode.value,
      };
    })
    .filter(notUndefinedOrNull)
    .sort((a, b) => b.value - a.value);

  if (isGeographic) {
    const segments = allSegments.slice(0, limit);

    if (allSegments.length > limit) {
      segments.push(
        allSegments.slice(limit, allSegments.length).reduce((acc, curr) => {
          return {
            name: intl.formatMessage({
              id: "SZGEj9",
              defaultMessage: "Outros",
              description: "Complemento do gráfico de receitas por países",
            }),
            value: acc.value + curr.value,
          };
        }),
      );
    }

    return segments;
  }

  return allSegments.slice(0, limit);
};

const getAllSegmentsForColumnChart = (
  kind: "GEOGRAPHIC" | "BUSINESS",
  selectedIndicator: string,
  selectedYear: number,
  segments: FinancialSegmentsFields["segments"],
  intl: IntlShape,
  limit: number = kind === "GEOGRAPHIC" ? 5 : 10,
) => {
  const standardizedCountries = genStandardizedCountries(intl);
  const isGeographic = kind === "GEOGRAPHIC";

  if (isGeographic) {
    const allSegments = segments
      .map((node) => {
        const ratioIndicator = node.ratioAdjustedIndicators.find(({ name }) => {
          return name === selectedIndicator;
        });
        if (!ratioIndicator) {
          return null;
        }
        const standardizedCountry = standardizedCountries.find((candidate) => {
          return candidate.morningstarStandardizedName === node.name;
        });
        const values = ratioIndicator.history
          .map((point) => ({
            x: getYear(parseISO(point.periodEndingDate).getTime()),
            y: point.value,
          }))
          .sort((a, b) => b.x - a.x);
        return {
          name: standardizedCountry ? standardizedCountry.name : node.name,
          yearValue: values.find((point) => point.x === selectedYear)?.y || 0,
          values,
        };
      })
      .filter(notUndefinedOrNull)
      .sort((a, b) => b.yearValue - a.yearValue);

    return [
      ...allSegments.sort((a, b) => b.yearValue - a.yearValue).slice(0, 5),
      allSegments
        .sort((a, b) => b.yearValue - a.yearValue)
        .slice(5, allSegments.length)
        .reduce(
          (acc, curr) => {
            const { values } = curr;

            return {
              name: intl.formatMessage({
                id: "SZGEj9",
                defaultMessage: "Outros",
                description: "Complemento do gráfico de receitas por países",
              }),
              values: acc.values.map((point) => {
                const history = values.find((node) => node.x === point.x);

                return {
                  x: point.x,
                  y: point.y + (history ? history.y : 0),
                };
              }),
            };
          },
          {
            name: "",
            values: allSegments
              .sort((a, b) => (a.values.length > b.values.length ? -1 : 1))[0]
              .values.map((point) => ({
                x: point.x,
                y: 0,
              })),
          },
        ),
    ];
  }

  return segments
    .map((node) => {
      const financialIndicator = node.financialIndicators.find(({ name }) => {
        return name === selectedIndicator;
      });
      if (!financialIndicator) {
        return null;
      }

      const values = financialIndicator.history
        .map((point) => ({
          x: getYear(parseISO(point.periodEndingDate).getTime()),
          y: point.value,
        }))
        .sort((a, b) => b.x - a.x);
      return {
        name: node.name,
        maxValue: Math.max(...values.map((point) => point.y)),
        values,
      };
    })
    .filter(notUndefinedOrNull)
    .sort((a, b) => b.maxValue - a.maxValue)
    .slice(0, limit);
};
